<template>
    <Modal :show="visible" width="small" :close-on-outside="true" @update:show="close">
        <h3 class="text-white text-base font-semibold mb-3">Convert object</h3>

        <div class="text-xs text-slate-300 mb-3">
            {{ currentLabel }} → {{ targetLabel }}
        </div>

        <label class="block text-xs text-slate-300 mb-1" for="conversion-target">Convert to</label>
        <select id="conversion-target" v-model="selectedTarget"
            class="w-full px-2 py-1.5 text-sm text-white bg-slate-800 rounded border border-slate-500 focus:border-blue-400 focus:outline-none">
            <option v-for="target in targets" :key="target" :value="target">
                {{ displayName(target) }}
            </option>
        </select>

        <div class="mt-3 rounded border border-slate-600 bg-slate-950/50 p-2">
            <div ref="host" class="conversion-preview-host"></div>
            <p v-if="error" class="text-xs text-red-300 text-center py-3">{{ error }}</p>
        </div>
        <p class="text-[11px] text-slate-300 mt-2">The object ID will remain: {{ sourceSnapshot?.haspid }}</p>

        <div class="flex justify-end gap-2 mt-4">
            <button type="button" class="px-3 py-1 text-xs text-slate-200 rounded border border-slate-500 hover:bg-slate-700"
                @click="close">Cancel</button>
            <button type="button" class="px-3 py-1 text-xs text-white rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
                :disabled="!selectedTarget || !!error" @click="confirm">OK</button>
        </div>
    </Modal>
</template>

<script>
import Konva from 'konva';
import Modal from '../Modal.vue';
import { objectTypelookup } from '../../utils/lookupTables';
import {
    buildObjectConversionPreview,
    getObjectTypeDisplayName,
} from '../../Hasp/properties/v2/objectConversion.js';

class PreviewLayer extends Konva.Layer {
    requestRender() { this.batchDraw(); }
    layerCallback() { }
    updateObjectMenu() { }
    updatePageZeroOverlay() { }
    updateTransformer() { }
    setSelectedNode() { }
    transformStart() { }
    transform() { }
    transformEnd() { }
    refreshBadgeRings() { }
    refreshBadgeConnections() { }
    refreshBadgeConnectionsForParent() { }
    requestConnectionAnimation() { }
    startBadgeConnectionDrag() { }
    updateBadgeConnectionDrag() { }
    finishBadgeConnectionDrag() { }
}

export default {
    name: 'ObjectConversionDialog',
    components: { Modal },
    props: {
        visible: { type: Boolean, default: false },
        currentLabel: { type: String, required: true },
        sourceSnapshot: { type: Object, required: true },
        targets: { type: Array, default: () => [] },
    },
    emits: ['close', 'confirm'],
    data() {
        return { selectedTarget: '', stage: null, layer: null, widgetNode: null, error: '' };
    },
    computed: {
        targetLabel() {
            return this.displayName(this.selectedTarget);
        },
    },
    watch: {
        visible(value) {
            if (value) {
                this.selectedTarget = this.targets[0] ?? '';
                this.$nextTick(() => this.renderPreview());
            } else {
                this.destroyPreview();
            }
        },
        selectedTarget() {
            if (this.visible) this.$nextTick(() => this.renderPreview());
        },
        targets: {
            deep: true,
            handler() {
                if (!this.targets.includes(this.selectedTarget)) this.selectedTarget = this.targets[0] ?? '';
            },
        },
    },
    beforeUnmount() {
        this.destroyPreview();
    },
    methods: {
        displayName(type) { return getObjectTypeDisplayName(type); },
        close() { this.$emit('close'); },
        confirm() {
            if (this.selectedTarget) this.$emit('confirm', this.selectedTarget);
        },
        renderPreview() {
            if (!this.$refs.host || !this.selectedTarget) return;
            this.destroyPreview();
            this.error = '';
            const WidgetClass = objectTypelookup[this.selectedTarget];
            if (!WidgetClass) {
                this.error = 'No Designer renderer is available for this object type.';
                return;
            }
            try {
                const config = buildObjectConversionPreview(this.sourceSnapshot, this.selectedTarget);
                const width = Math.max(1, Number(config.w ?? config.width ?? 100));
                const height = Math.max(1, Number(config.h ?? config.height ?? 40));
                const previewConfig = {
                    ...config,
                    haspid: config.id ?? this.sourceSnapshot.haspid,
                    x: 0,
                    y: 0,
                    width,
                    height,
                    show_id: false,
                };
                this.stage = new Konva.Stage({ container: this.$refs.host, width: 280, height: 120 });
                this.layer = new PreviewLayer({ listening: false });
                this.stage.add(this.layer);
                this.widgetNode = new WidgetClass(previewConfig);
                this.layer.add(this.widgetNode);
                const scale = Math.min(1, 260 / width, 100 / height);
                this.widgetNode.scale({ x: scale, y: scale });
                this.widgetNode.position({
                    x: (280 - width * scale) / 2,
                    y: (120 - height * scale) / 2,
                });
                this.layer.draw();
            } catch (conversionError) {
                this.error = conversionError?.message || 'Preview could not be rendered.';
                this.destroyPreview();
            }
        },
        destroyPreview() {
            this.widgetNode?.destroy();
            this.widgetNode = null;
            this.layer?.destroy();
            this.layer = null;
            this.stage?.destroy();
            this.stage = null;
        },
    },
};
</script>

<style scoped>
.conversion-preview-host {
    width: 280px;
    height: 120px;
    max-width: 100%;
    margin: 0 auto;
}
</style>
